import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { useAuth } from "@/contexts/AuthContext";
import { Lock } from "lucide-react";

interface SellerRestrictedDashboardTileProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  to: string;
  isPrimary?: boolean;
  count?: number;
  isRestricted?: boolean;
  tourId?: string;
}

const SellerRestrictedDashboardTile = ({
  title,
  description,
  icon,
  to,
  isPrimary = false,
  count,
  isRestricted = false,
  tourId
}: SellerRestrictedDashboardTileProps) => {
  const { subscriptionStatus } = useSubscription();
  const { isBuyer } = useBuyer();
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  // Only show for sellers (not buyers, has seller profile) with expired status
  const isSeller = !isBuyer && profile;
  const isSellerExpired = isSeller && subscriptionStatus === 'expired';
  const shouldRestrict = isRestricted && isSellerExpired;

  const handleClick = (e: React.MouseEvent) => {
    if (shouldRestrict) {
      e.preventDefault();
      navigate('/seller/upgrade');
    }
  };

  const TileContent = () => (
    <Card 
      data-tour={tourId}
      className={cn(
        "h-full transition-transform",
        shouldRestrict ? "opacity-60 cursor-not-allowed" : "hover-scale",
        isPrimary ? "bg-godyspo-gold text-black border-godyspo-gold" : "bg-card"
      )}
    >
      <CardContent className="flex flex-col h-full p-4 md:p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className={cn(
            "text-lg font-semibold",
            isPrimary ? "text-black" : "text-white"
          )}>
            {title}
            {count !== undefined && (
              <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs rounded-full bg-black/10">
                {count}
              </span>
            )}
          </h3>
          <div className={cn(
            "text-2xl flex items-center gap-2",
            isPrimary ? "text-black" : "text-godyspo-gold"
          )}>
            {shouldRestrict && <Lock className="h-5 w-5" />}
            {icon}
          </div>
        </div>
        <p className={cn(
          "text-sm",
          isPrimary ? "text-black/80" : "text-muted-foreground"
        )}>
          {shouldRestrict ? "Upgrade to access this feature" : description}
        </p>
      </CardContent>
    </Card>
  );

  if (shouldRestrict) {
    return (
      <div onClick={handleClick} className="block h-full">
        <TileContent />
      </div>
    );
  }

  return (
    <Link to={to} className="block h-full">
      <TileContent />
    </Link>
  );
};

export default SellerRestrictedDashboardTile;